import React, {useEffect, useReducer, useState} from "react";
import {
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    addDays,
    format,
    isSameMonth,
    isToday,
    addMonths,
    subMonths,
} from "date-fns";
import {ArrowDropDownIcon, ArrowDropUpIcon, Checkbox, Tab, Tag} from "omoplay";
import MessageWrapPreview from "./volatility-message/content/preview/MessageWrapPreview";
import {
    includeMonthlyMessage,
    listMessageCalendar,
    listSpecialDate,
    monthlyMessagesAll
} from "../api/message/message-calendar.api";
import moment from "moment";
import {useRecoilValue} from "recoil";
import {channelSelector} from "../store/selector/selector";


function MessageCalendar() {
    const channelId = useRecoilValue(channelSelector)
    const [currentDate, setCurrentDate] = useState(new Date())
    const [messages, setMessages] = useState([])
    const [selectedMessage, setSelectedMessage] = useState(null)
    const [specialDates, setSpecialDates] = useState([])
    const [hideSettings, setHideSettings] = useState(true)
    const [monthlyMessages, setMonthlyMessages] = useState([])
    const [monthlyType, setMonthlyType] = useState("HOLIDAY")
    const [monthlyPreviewMessages, setMonthlyPreviewMessages] = useState({})
    const [sync, setSync] = useState(false)

    useEffect(() => {
        listMessageCalendar(currentDate.getFullYear(), currentDate.getMonth() + 1).then(({data}) => {
            setMessages(data)
        })
        listSpecialDate(currentDate.getFullYear(), currentDate.getMonth() + 1).then(({data}) => {
            setSpecialDates(data)
        })
    }, [currentDate]);

    useEffect(() => {
        monthlyMessagesAll().then(({data}) => setMonthlyMessages(data))
    }, [sync, monthlyType]);

    const renderHeader = () => (
        <div className="flex justify-between items-center p-4">
            <button onClick={() => setCurrentDate(subMonths(currentDate, 1))}>
                ◀
            </button>
            <h2 className="text-xl font-bold">
                {format(currentDate, "yyyy년 M월")}
            </h2>
            <button onClick={() => setCurrentDate(addMonths(currentDate, 1))}>
                ▶
            </button>
        </div>
    );

    const renderDays = () => {
        const weekDays = ["일", "월", "화", "수", "목", "금", "토"];
        return (
            <div className="grid grid-cols-7 py-2 border-t border-b bg-gray-50">
                {weekDays.map((day) => (
                    <div key={day} className={`text-center font-semibold${day === "토" ? ' text-primary' : (day === "일" ? ' text-error' : '')}`}>
                        {day}
                    </div>
                ))}
            </div>
        );
    };

    const renderCells = () => {
        const monthStart = startOfMonth(currentDate);
        const monthEnd = endOfMonth(monthStart);
        const startDate = startOfWeek(monthStart);
        const endDate = endOfWeek(monthEnd);

        const rows = [];
        let days = [];
        let day = startDate;

        while (day <= endDate) {
            for (let i = 0; i < 7; i++) {
                const formattedDate = format(day, "d");
                const hasMessages = messages[formattedDate];
                const today = isToday(day);
                const isCurrentMonth = isSameMonth(day, monthStart);
                const specialDate = specialDates.find((value) => value.locdate === format(day, "yyyyMMdd"))

                days.push(
                    <div
                        key={day}
                        className={`border p-2 min-h-32 text-sm ${
                            !isCurrentMonth ? "text-gray-400 bg-gray-50" : ""
                        }`}
                    >
                        <div className="flex justify-center mb-1">
                            <div
                                className={`w-6 h-6 flex items-center justify-center text-sm font-bold ${
                                    today ? "bg-red-500 text-white rounded-full" : ""
                                }`}
                            >
                                {formattedDate}
                            </div>
                        </div>
                        {specialDate && (<Tag color="success">{specialDate.dateName}</Tag>)}
                        {isCurrentMonth &&
                            hasMessages?.map((message, idx) => (
                                <div className="mt-1 pointer"
                                     onClick={() => {
                                         setSelectedMessage(message)
                                     }}>
                                    <Tag key={idx} color={message.type === "recommend" ? "info" : "warning"}
                                         size="small">{message.name}</Tag>
                                </div>
                                ))}
                    </div>
                );
                day = addDays(day, 1);
            }

            rows.push(
                <div className="grid grid-cols-7" key={day}>
                    {days}
                </div>
            );
            days = [];
        }

        return <div>{rows}</div>;
    };

    const handleSync = async (checked) => {
        const tasks = monthlyMessages.filter(message => message.type === monthlyType)
            .map(message => includeMonthlyMessage(message.id, checked))

        await Promise.all(tasks)

        setMonthlyMessages([])
        setSync(!sync)
    }


    return (
        <div className="p-8">
            <div className="flex gap-8">
                <div className="flex-col flex-align-center gap-4">
                    <div className="flex-align-center gap-2">
                        <div className="flex-align-center bg-white w-[32px] h-[32px] border rounded-[36px] pointer"
                             onClick={() => {
                                 setHideSettings(!hideSettings)
                             }}>
                            {hideSettings ? <ArrowDropDownIcon className="flex-center-all w-full"/> :
                                <ArrowDropUpIcon className="flex-center-all w-full"/>}
                        </div>
                        <div className="text-2xl">온보딩 메시지 설정</div>
                    </div>
                    <div className={`${hideSettings ? 'hidden' : '' }`}>
                        <Tab variants="input" items={[{label: "공휴일", value: "HOLIDAY", selected: true}, {label: "잡절", value: "SUNDRY_DAY"}, {label: "기타", value: "ETC"}]}
                             onChanged={(item) => setMonthlyType(item.value)} />
                        <div className="mt-4">
                            <div className="flex-align-center gap-8 mb-4">
                                <div className="flex-1 text-xl">전체</div>
                                <div>
                                    <Checkbox
                                        checked={monthlyMessages
                                            .filter(message => message.type === monthlyType)
                                            .every(message => message.include.channelIds.includes(channelId))}
                                        onChecked={(checked) => handleSync(checked)} />
                                </div>
                            </div>
                            {
                                monthlyMessages.filter(message => message.type === monthlyType).map(message => (
                                    <div className="flex-align-center gap-8">
                                        <div className="flex-1 text-xl text-primary pointer"
                                             onClick={() => {
                                                 setMonthlyPreviewMessages(message.message)
                                             }}
                                        >{message.category}</div>
                                        <Checkbox key={`${monthlyType}-${message.id}`}
                                                  checked={message.include.channelIds.includes(channelId)}
                                                  onChecked={(checked) => {
                                                      includeMonthlyMessage(message.id, checked).then(r => r)
                                                  }} />
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                </div>
                <div className={`${hideSettings ? 'hidden' : '' }`}>
                    {
                        <MessageWrapPreview messageData={monthlyPreviewMessages} />
                    }
                </div>
            </div>
            <div className="flex-align-center gap-16">
                <div className="flex-1 max-w-4xl mt-10 p-4 border rounded shadow bg-white">
                    {renderHeader()}
                    {renderDays()}
                    {renderCells()}
                </div>
                <div>
                    {
                        selectedMessage && (
                            selectedMessage?.type === "recommend" ? (
                                <div className="flex-col bg-white p-8 rounded shadow">
                                    <div className="flex-col gap-4 flex-1 pr-2">
                                        {
                                            selectedMessage?.category && (
                                                <div className="flex-col gap-2 bg-depth1 p-4 rounded-[8px]">
                                                    <div>메시지 성격: {selectedMessage?.category}</div>
                                                </div>
                                            )
                                        }
                                        <div className="flex-col gap-2 bg-depth1 p-4 rounded-[8px]">
                                            <div>
                                                발송시간: {moment(selectedMessage?.sendAt).format('YYYY-MM-DD HH:mm')}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex-1 pl-2">
                                        <MessageWrapPreview messageData={selectedMessage?.messageData} />
                                    </div>
                                </div>
                            ) : (
                                <div className="flex-center-all">
                                    <MessageWrapPreview messageData={selectedMessage?.messageData} />
                                </div>
                            )
                        )
                    }
                </div>
            </div>
        </div>
    );
}

export default MessageCalendar;
